import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DyanamicDropdown from 'components/Elements/DynamicDropdown';
import { useFilters } from '../FilterContext';
import CustomDateRange from 'pages/Services/ServiceLists/CustomDateRange';
import { SHOW_ALL_OPTION } from '../constants';
import { customFromattedDate, convertDateToUTC } from 'helpers/utils';
import { changeDate } from 'helpers/date';
import { getSiteApplicationDetail } from 'services/clients/sites/siteApplications/siteApplicationDetails';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
  subYears,
  startOfYear,
  endOfYear,
  getWeek,
  addDays
} from "date-fns";

// Moved from constants/Analytics/index.js
export const filterByDateOptions = (
  todayDate,
  customCaptions,
  weekStartDay = 1,
  includeTomorrow = false
) => {
  // Convert our week day (0-6, Monday-Sunday) to date-fns week day (0-6, Sunday-Saturday)
  const dateFnsWeekStartDay = (weekStartDay + 1) % 7;

  // Get current week number
  const thisWeekNumber = getWeek(todayDate, { weekStartsOn: dateFnsWeekStartDay });
  // Get last week number
  const lastWeekNumber = getWeek(subWeeks(todayDate, 1), { weekStartsOn: dateFnsWeekStartDay });


  const options = [
    { ...SHOW_ALL_OPTION, fromDate: "", toDate: "" },
    {
      id: 1,
      name: "Today",
      fromDate: convertDateToUTC(startOfDay(todayDate)),
      toDate: convertDateToUTC(endOfDay(todayDate)),
    },
    {
      id: 12,
      name: "Yesterday",
      fromDate: convertDateToUTC(startOfDay(subDays(todayDate, 1))),
      toDate: convertDateToUTC(endOfDay(subDays(todayDate, 1))),
    },
    {
      id: 2,
      name: `This Week (${thisWeekNumber})`,
      fromDate: convertDateToUTC(startOfWeek(todayDate, { weekStartsOn: dateFnsWeekStartDay })),
      toDate: convertDateToUTC(endOfWeek(todayDate, { weekStartsOn: dateFnsWeekStartDay })),
    },
    {
      id: 9,
      name: `Last Week (${lastWeekNumber})`,
      fromDate: convertDateToUTC(startOfWeek(subWeeks(todayDate, 1), { weekStartsOn: dateFnsWeekStartDay })),
      toDate: convertDateToUTC(endOfWeek(subWeeks(todayDate, 1), { weekStartsOn: dateFnsWeekStartDay })),
    },
    {
      id: 70,
      name: "Next 7 Days",
      fromDate: convertDateToUTC(startOfDay(todayDate)),
      toDate: convertDateToUTC(endOfDay(addDays(todayDate, 6))),
    },
    {
      id: 140,
      name: "Next 14 Days",
      fromDate: convertDateToUTC(startOfDay(todayDate)),
      toDate: convertDateToUTC(endOfDay(addDays(todayDate, 13))),
    },
    {
      id: 3,
      name: "Last Month",
      fromDate: convertDateToUTC(startOfMonth(subMonths(todayDate, 1))),
      toDate: convertDateToUTC(endOfMonth(subMonths(todayDate, 1))),
    },
    {
      id: 11,
      name: "Last Year",
      fromDate: convertDateToUTC(startOfYear(subYears(todayDate, 1))),
      toDate: convertDateToUTC(endOfYear(subYears(todayDate, 1))),
    },
    {
      id: 7,
      name: "Last 7 Days",
      fromDate: convertDateToUTC(subDays(todayDate, 7)),
      toDate: convertDateToUTC(endOfDay(todayDate)),
    },
    {
      id: 8,
      name: "Last 14 Days",
      fromDate: convertDateToUTC(subDays(todayDate, 14)),
      toDate: convertDateToUTC(endOfDay(todayDate)),
    },
    {
      id: 6,
      name: "Last 30 Days",
      fromDate: convertDateToUTC(subDays(todayDate, 30)),
      toDate: convertDateToUTC(endOfDay(todayDate)),
    },
    {
      id: 10,
      name: "Last 90 Days",
      fromDate: convertDateToUTC(subDays(todayDate, 90)),
      toDate: convertDateToUTC(endOfDay(todayDate)),
    },
    { id: 5, name: "Custom Range..." },
  ];
  
  // Only include Tomorrow option if explicitly requested
  if (includeTomorrow) {
    options.splice(3, 0, {
      id: 13,
      name: "Tomorrow",
      fromDate: convertDateToUTC(startOfDay(addDays(todayDate, 1))),
      toDate: convertDateToUTC(endOfDay(addDays(todayDate, 1))),
    });
  }
  
  return options;
};

export const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export const TimeframeFilter = () => {
  const { filterValues, updateFilter, getInitialFilterValue, config } = useFilters();
  const [openCustomDatePopup, setOpenCustomDatePopup] = useState(false);
  const [customDate, setCustomDate] = useState({ from: '', to: '' });
  const [isCustomDateRangeError, setCustomDateRangeError] = useState({ from: false, to: false });
  const [weekStartDay, setWeekStartDay] = useState(1); // Default to Monday
  
  // Get weekStartDay from site application details
  useEffect(() => {
    const fetchSiteApplicationDetails = async () => {
      try {
        const { siteAppID } = JSON.parse(localStorage.getItem("me")) || JSON.parse(sessionStorage.getItem("me"));
        const result = await getSiteApplicationDetail(siteAppID);
        setWeekStartDay(result.data.weekStartDay);
      } catch (error) {
        console.error("Error fetching site application details:", error);
      }
    };
    fetchSiteApplicationDetails();
  }, []);

  // Filter options based on config.filters.timeframe.allowedRanges if specified
  const timeframeOptions = React.useMemo(() => {
    // Check if we have a specific allowedRanges configuration
    const timeframeConfig = config?.filters?.timeframe;
    const hasAllowedRanges = timeframeConfig && 
                            typeof timeframeConfig === 'object' && 
                            timeframeConfig.allowedRanges;
    
    // Only include tomorrow in the default options if it's explicitly included in allowedRanges
    const includeTomorrow = hasAllowedRanges && timeframeConfig.allowedRanges.includes('tomorrow');
    const allOptions = filterByDateOptions(new Date(), null, weekStartDay, includeTomorrow);
    
    if (hasAllowedRanges) {
      const allowedRanges = timeframeConfig.allowedRanges;
      
      // Map allowedRanges values to actual filter IDs
      const rangeToIdMap = {
        'today': 1,
        'yesterday': 12,
        'tomorrow': 13,
        'this-week': 2,
        'last-week': 9,
        'last-month': 3,
        'last-year': 11,
        'last-7-days': 7,
        'last-14-days': 8,
        'last-30-days': 6,
        'last-90-days': 10,
        'custom': 5,
        'next-7-days': 70,
        'next-14-days': 140,
      };
      
      // Always include "Show All" option
      const filteredOptions = [allOptions[0]];
      
      // Add options that match the allowed ranges
      allowedRanges.forEach(range => {
        const id = rangeToIdMap[range];
        if (id !== undefined) {
          const option = allOptions.find(opt => opt.id === id);
          if (option) {
            filteredOptions.push(option);
          }
        }
      });
      
      return filteredOptions;
    }
    
    return allOptions;
  }, [weekStartDay, config?.filters?.timeframe]);

  // Helper function to parse custom date range from URL/storage
  const parseCustomDateRange = (customId) => {
    if (typeof customId === 'string' && customId.startsWith('custom:')) {
      const parts = customId.split(':');
      if (parts.length === 3) {
        const fromDate = parts[1];
        const toDate = parts[2];
        const formattedCustomDate = customFromattedDate({ from: fromDate, to: toDate });
        
        return {
          id: customId,
          name: 'Customized Date',
          fromDate: formattedCustomDate.fromDate,
          toDate: formattedCustomDate.toDate
        };
      }
    }
    return null;
  };

  // Set timeframe from URL or default when options are available
  useEffect(() => {
    if (!filterValues.timeframe && timeframeOptions.length > 0) {
      // If config has a defaultRange, try to use that
      const timeframeConfig = config?.filters?.timeframe;
      let defaultOption = timeframeOptions[0]; // Default to first option
      
      if (timeframeConfig && typeof timeframeConfig === 'object' && timeframeConfig.defaultRange) {
        const rangeToIdMap = {
          'today': 1,
          'yesterday': 12,
          'tomorrow': 13,
          'this-week': 2,
          'last-week': 9,
          'last-month': 3,
          'last-year': 11,
          'last-7-days': 7,
          'last-14-days': 8,
          'last-30-days': 6,
          'last-90-days': 10,
          'next-7-days': 70,
          'next-14-days': 140,
        };
        
        const defaultId = rangeToIdMap[timeframeConfig.defaultRange];
        if (defaultId !== undefined) {
          const option = timeframeOptions.find(opt => opt.id === defaultId);
          if (option) {
            defaultOption = option;
          }
        }
      }
      
      // Custom finder function that handles both normal options and custom date ranges
      const findTimeframeOption = (options, id) => {
        // First try to find in normal options
        const normalOption = options.find(opt => 
          opt.id === id || 
          opt.id === parseInt(id) || 
          String(opt.id) === String(id)
        );
        
        if (normalOption) return normalOption;
        
        // If not found and it's a custom date range, parse it
        return parseCustomDateRange(id);
      };
      
      const selectedOption = getInitialFilterValue('timeframe', timeframeOptions, defaultOption, findTimeframeOption);
      updateFilter('timeframe', selectedOption, {
        startDate: selectedOption.fromDate || '',
        endDate: selectedOption.toDate || ''
      });
    }
  }, [timeframeOptions, filterValues.timeframe, getInitialFilterValue, updateFilter, config?.filters?.timeframe]);

  // Don't render until we have a timeframe value
  if (!filterValues.timeframe) return null;

  const handleCustomDateChange = (type, e) => {
    setCustomDate({ ...customDate, [type]: e.target.value });
  };

  const handleCloseCustomDate = () => {
    setOpenCustomDatePopup(false);
    setCustomDateRangeError({ from: false, to: false });
  };

  const handleCustomDateSubmit = (e) => {
    e.preventDefault();
    const isFromDateEmpty = customDate.from === '';
    const isToDateEmpty = customDate.to === '';
    
    if (isFromDateEmpty || isToDateEmpty) {
      setCustomDateRangeError({ from: isFromDateEmpty, to: isToDateEmpty });
      return;
    }

    const formattedCustomDate = customFromattedDate(customDate);
    // Create a serialized ID for custom date range: custom:fromDate:toDate
    const customId = `custom:${customDate.from}:${customDate.to}`;
    
    const customTimeframe = {
      ...filterValues.timeframe,
      ...formattedCustomDate,
      name: 'Customized Date',
      id: customId
    };

    updateFilter('timeframe', customTimeframe, {
      startDate: formattedCustomDate.fromDate,
      endDate: formattedCustomDate.toDate
    });

    handleCloseCustomDate();
  };

  // Check if custom date option should be hidden
  const timeframeConfig = config?.filters?.timeframe;
  const showCustomRange = !(timeframeConfig && 
                           typeof timeframeConfig === 'object' && 
                           timeframeConfig.allowCustomRange === false);

  // Filter out custom option if not allowed
  const displayOptions = showCustomRange 
    ? timeframeOptions 
    : timeframeOptions.filter(option => option.id !== 5);

  const selectedValue = filterValues.timeframe?.value;

  return (
    <>
      <Grid item lg={3} md={6} xs={12}>
        <DyanamicDropdown
          dataSource={displayOptions}
          columns={[{ name: 'name', id: 1, minWidth: '130px' }]}
          showHeader={false}
          width="100%"
          placeholder="Select Timeframe"
          onChange={item => {
            if (item.id === 5) { // Custom range
              // Initialize custom date with existing values if available
              const currentTimeframe = filterValues.timeframe?.value;
              if (currentTimeframe && 
                  (currentTimeframe.id === 5 || 
                   (typeof currentTimeframe.id === 'string' && currentTimeframe.id.startsWith('custom:')))) {
                // Extract dates from existing custom range
                let fromDate = '';
                let toDate = '';
                
                if (typeof currentTimeframe.id === 'string' && currentTimeframe.id.startsWith('custom:')) {
                  // Parse from custom ID format: custom:fromDate:toDate
                  const parts = currentTimeframe.id.split(':');
                  if (parts.length === 3) {
                    fromDate = parts[1];
                    toDate = parts[2];
                  }
                } else if (currentTimeframe.fromDate && currentTimeframe.toDate) {
                  // Extract from formatted dates - convert back to input format
                  const fromDateObj = new Date(currentTimeframe.fromDate);
                  const toDateObj = new Date(currentTimeframe.toDate);
                  fromDate = fromDateObj.toISOString().split('T')[0];
                  toDate = toDateObj.toISOString().split('T')[0];
                }
                
                setCustomDate({ from: fromDate, to: toDate });
              } else {
                // Reset to empty if no existing custom range
                setCustomDate({ from: '', to: '' });
              }
              
              setOpenCustomDatePopup(true);
              return;
            }
            updateFilter('timeframe', item, {
              startDate: item.fromDate || '',
              endDate: item.toDate || ''
            });
          }}
          selectdValueToshow="name"
          selectedValue={{
            ...selectedValue,
            name: (selectedValue?.id === 5 || (typeof selectedValue?.id === 'string' && selectedValue?.id.startsWith('custom:'))) && selectedValue?.fromDate
              ? `${changeDate(selectedValue?.fromDate)} - ${changeDate(selectedValue?.toDate)}`
              : selectedValue?.name
          }}
          label="Filter by Timeframe"
          icon={<FilterListIcon style={{ color: 'rgb(48,122,215)' }} />}
          required={false}
          showBorderColor
          showSearchBox={false}
        />
      </Grid>
      
      {showCustomRange && (
        <CustomDateRange
          open={openCustomDatePopup}
          handleChange={handleCustomDateChange}
          customDate={customDate}
          closeHandler={handleCloseCustomDate}
          onSubmit={handleCustomDateSubmit}
          isError={isCustomDateRangeError}
          isLoading={false}
        />
      )}
    </>
  );
}; 
